import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, ChevronDown } from 'lucide-react';
import patientCallingService from '../../../services/patientCalling.service';
import CommonDropdown from '../../Common/CommonDropdown';
import styles from './ManualAddPatient.module.css';

interface Props {
    callType?: string;
    onClose: () => void;
    onSaved: () => void;
}

const REASON_OPTIONS = [
    'Need Insurance Information',
    'Need Active Insurance Information',
    'Patient Needs to Contact Payer',
    'Claim Denied',
    'Other',
];

const REFERENCE_OPTIONS = [
    { value: 'Copay', label: 'Copay' },
    { value: 'Coinsurance', label: 'Coinsurance' },
    { value: 'Deductible', label: 'Deductible' },
    { value: 'Other', label: 'Other' },
];

const REASON_FOR_CALL_OPTIONS = REASON_OPTIONS.map(o => ({ value: o, label: o }));

const ManualAddPatient: React.FC<Props> = ({ callType: initialCallType = 'payment_reminder', onClose, onSaved }) => {
    const [callType, setCallType] = useState<string>(initialCallType);
    const isPayment = callType === 'payment_reminder';
    const [form, setForm] = useState<Record<string, any>>({});
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showScrollDown, setShowScrollDown] = useState(false);
    const bodyRef = useRef<HTMLDivElement>(null);

    const set = (field: string, val: any) => setForm(prev => ({ ...prev, [field]: val }));

    const checkScroll = useCallback(() => {
        const el = bodyRef.current;
        if (!el) { setShowScrollDown(false); return; }
        const hasOverflow = el.scrollHeight > el.clientHeight + 4;
        const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 8;
        setShowScrollDown(hasOverflow && !atBottom);
    }, []);

    useEffect(() => {
        checkScroll();
        const el = bodyRef.current;
        if (!el || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => checkScroll());
        ro.observe(el);
        Array.from(el.children).forEach(child => ro.observe(child));
        return () => ro.disconnect();
    }, [checkScroll, isPayment]);

    const scrollBodyDown = () => {
        const el = bodyRef.current;
        if (!el) return;
        el.scrollBy({ top: el.clientHeight * 0.8, behavior: 'smooth' });
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!form.patient_name?.trim()) { setError('Patient name is required'); return; }
        setLoading(true);
        setError('');
        try {
            await patientCallingService.createServiceLog(form);
            onSaved();
        } catch (e: any) {
            setError(e.message || 'Failed to save patient');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className={styles.drawerOverlay} onClick={onClose}>
            <div className={styles.drawer} onClick={e => e.stopPropagation()}>
                <div className={styles.drawerHeader}>
                    <div className={styles.drawerHeaderLeft}>
                        <h2 className={styles.drawerTitle}>Add Patient</h2>
                        <div className={styles.callTypeToggle}>
                            <button
                                type="button"
                                className={`${styles.callTypeToggleBtn} ${isPayment ? styles.callTypeToggleBtnActive : ''}`}
                                onClick={() => setCallType('payment_reminder')}
                            >
                                Payment Reminder
                            </button>
                            <button
                                type="button"
                                className={`${styles.callTypeToggleBtn} ${!isPayment ? styles.callTypeToggleBtnActive : ''}`}
                                onClick={() => setCallType('patient_info_gathering')}
                            >
                                Info Gathering
                            </button>
                        </div>
                    </div>
                    <button className={styles.closeBtn} onClick={onClose}><X size={18} /></button>
                </div>

                <form onSubmit={handleSubmit} className={styles.drawerForm}>
                    <div className={styles.drawerBody} ref={bodyRef} onScroll={checkScroll}>
                    {error && <div className={styles.errorBanner}>{error}</div>}

                    {/* Patient Info */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionLabel}>Patient Info</div>
                        <div className={styles.formGrid2}>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Patient Name <span className={styles.required}>*</span></label>
                                <input className={styles.input} value={form.patient_name || ''} onChange={e => set('patient_name', e.target.value)} placeholder="Last, First" />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Patient ID</label>
                                <input className={styles.input} value={form.patient_id || ''} onChange={e => set('patient_id', e.target.value)} placeholder="e.g. P1497" />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Date of Birth</label>
                                <input type="date" className={styles.input} value={form.dob || ''} onChange={e => set('dob', e.target.value)} />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Phone Number <span className={styles.required}>*</span></label>
                                <input className={styles.input} value={form.phone_number || ''} onChange={e => set('phone_number', e.target.value)} placeholder="(555) 000-0000" />
                            </div>
                        </div>
                        {isPayment && (
                            <div className={styles.formGrid2}>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Address Line 1</label>
                                    <input className={styles.input} value={form.address_line_1 || ''} onChange={e => set('address_line_1', e.target.value)} />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Address Line 2</label>
                                    <input className={styles.input} value={form.address_line_2 || ''} onChange={e => set('address_line_2', e.target.value)} />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Insurance */}
                    <div className={styles.formSection}>
                        <div className={styles.sectionLabel}>Insurance</div>
                        <div className={styles.formGrid2}>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Primary Insurance</label>
                                <input className={styles.input} value={form.insurance_name || ''} onChange={e => set('insurance_name', e.target.value)} />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Primary Insurance ID</label>
                                <input className={styles.input} value={form.insurance_id || ''} onChange={e => set('insurance_id', e.target.value)} />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Secondary Insurance</label>
                                <input className={styles.input} value={form.secondary_insurance || ''} onChange={e => set('secondary_insurance', e.target.value)} />
                            </div>
                            <div className={styles.formGroup}>
                                <label className={styles.label}>Secondary Insurance ID</label>
                                <input className={styles.input} value={form.secondary_insurance_id || ''} onChange={e => set('secondary_insurance_id', e.target.value)} />
                            </div>
                        </div>
                    </div>

                    {isPayment ? (
                        <>
                            {/* Provider Info */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionLabel}>Provider Info</div>
                                <div className={styles.formGrid2}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Facility Name</label>
                                        <input className={styles.input} value={form.facility_name || ''} onChange={e => set('facility_name', e.target.value)} placeholder="Where care was given" />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Check Payable To</label>
                                        <input className={styles.input} value={form.check_payable_to || ''} onChange={e => set('check_payable_to', e.target.value)} placeholder="Billing provider name" />
                                    </div>
                                </div>
                            </div>

                            {/* Statement Details */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionLabel}>Statement Details</div>
                                <div className={styles.formGrid2}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Statement Date</label>
                                        <input type="date" className={styles.input} value={form.statement_date || ''} onChange={e => set('statement_date', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Date(s) of Service</label>
                                        <input className={styles.input} value={form.dos || ''} onChange={e => set('dos', e.target.value)} placeholder="e.g. 03/15/2022" />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Charge</label>
                                        <input type="number" step="0.01" className={styles.input} value={form.charge || ''} onChange={e => set('charge', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Primary Ins. Payment</label>
                                        <input type="number" step="0.01" className={styles.input} value={form.primary_payment || ''} onChange={e => set('primary_payment', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Secondary Ins. Payment</label>
                                        <input type="number" step="0.01" className={styles.input} value={form.secondary_payment || ''} onChange={e => set('secondary_payment', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Patient Payment</label>
                                        <input type="number" step="0.01" className={styles.input} value={form.patient_payment || ''} onChange={e => set('patient_payment', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Adjustment</label>
                                        <input type="number" step="0.01" className={styles.input} value={form.adjustment || ''} onChange={e => set('adjustment', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Total Balance <span className={styles.required}>*</span></label>
                                        <input type="number" step="0.01" className={styles.input} value={form.total_balance || ''} onChange={e => set('total_balance', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Current Due</label>
                                        <input type="number" step="0.01" className={styles.input} value={form.current_due || ''} onChange={e => set('current_due', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Over 30 Days</label>
                                        <input type="number" step="0.01" className={styles.input} value={form.over_30_days || ''} onChange={e => set('over_30_days', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Over 60 Days</label>
                                        <input type="number" step="0.01" className={styles.input} value={form.over_60_days || ''} onChange={e => set('over_60_days', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Over 90 Days</label>
                                        <input type="number" step="0.01" className={styles.input} value={form.over_90_days || ''} onChange={e => set('over_90_days', e.target.value)} />
                                    </div>
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Reference / Reason</label>
                                    <CommonDropdown
                                        value={form.reference || ''}
                                        onChange={(v) => set('reference', v || '')}
                                        options={REFERENCE_OPTIONS}
                                        placeholder="— Select —"
                                        size="md"
                                    />
                                </div>
                            </div>
                        </>
                    ) : (
                        <>
                            {/* Claim Details */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionLabel}>Claim Details</div>
                                <div className={styles.formGrid2}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Visit ID</label>
                                        <input className={styles.input} value={form.visit_id || ''} onChange={e => set('visit_id', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Date of Service</label>
                                        <input className={styles.input} value={form.dos || ''} onChange={e => set('dos', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>CPT Code</label>
                                        <input className={styles.input} value={form.cpt_code || ''} onChange={e => set('cpt_code', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>ICD Code</label>
                                        <input className={styles.input} value={form.icd_code || ''} onChange={e => set('icd_code', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Billed Amount</label>
                                        <input type="number" step="0.01" className={styles.input} value={form.billed_amount || ''} onChange={e => set('billed_amount', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Balance</label>
                                        <input type="number" step="0.01" className={styles.input} value={form.total_balance || ''} onChange={e => set('total_balance', e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            {/* AR Context */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionLabel}>AR Context</div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>Reason for Call <span className={styles.required}>*</span></label>
                                    <CommonDropdown
                                        value={form.reason_for_call || ''}
                                        onChange={(v) => set('reason_for_call', v || '')}
                                        options={REASON_FOR_CALL_OPTIONS}
                                        placeholder="— Select —"
                                        size="md"
                                        isSearchable
                                    />
                                </div>
                                <div className={styles.formGroup}>
                                    <label className={styles.label}>AR Comments</label>
                                    <textarea className={styles.textarea} rows={4} value={form.ar_comments || ''} onChange={e => set('ar_comments', e.target.value)} placeholder="Full biller notes..." />
                                </div>
                                <div className={styles.formGrid2}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>AR Status</label>
                                        <input className={styles.input} value={form.ar_status || ''} onChange={e => set('ar_status', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>AR Assigned To</label>
                                        <input className={styles.input} value={form.ar_assigned_to || ''} onChange={e => set('ar_assigned_to', e.target.value)} />
                                    </div>
                                </div>
                            </div>

                            {/* Provider */}
                            <div className={styles.formSection}>
                                <div className={styles.sectionLabel}>Provider</div>
                                <div className={styles.formGrid2}>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Facility / Location</label>
                                        <input className={styles.input} value={form.facility_name || ''} onChange={e => set('facility_name', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Rendering Provider</label>
                                        <input className={styles.input} value={form.rendering_provider || ''} onChange={e => set('rendering_provider', e.target.value)} />
                                    </div>
                                    <div className={styles.formGroup}>
                                        <label className={styles.label}>Billing Provider</label>
                                        <input className={styles.input} value={form.billing_provider || ''} onChange={e => set('billing_provider', e.target.value)} />
                                    </div>
                                </div>
                            </div>
                        </>
                    )}

                    </div>
                    {showScrollDown && (
                        <button
                            type="button"
                            className={styles.scrollIndicator}
                            onClick={scrollBodyDown}
                            aria-label="Scroll down to see more fields"
                            title="Scroll down"
                        >
                            <ChevronDown size={16} />
                        </button>
                    )}
                    <div className={styles.drawerFooter}>
                        <button type="button" className={styles.outlineBtn} onClick={onClose}>Cancel</button>
                        <button type="submit" className={styles.primaryBtn} disabled={loading}>
                            {loading ? 'Saving...' : 'Save Patient'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ManualAddPatient;
